import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import LayoutComponent from '../BaseScreen';
import { Upload, Search, FileText, AlertCircle, CheckCircle, Clock, Trash2, Download, Mail, Zap, Activity, XCircle, BarChart2, StopCircle } from 'react-feather';
import axios from 'axios';
import { useAuth } from '../../Context/AuthContext';
import baseUrl from '../../Utils/baseUrl';

interface ProcessingStatus {
  status: 'idle' | 'processing' | 'completed' | 'error';
  processed: number;
  errors: number;
  total: number;
  message: string;
}

// Stat Card Component matching IntakeAnalytics style
const StatCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'teal' | 'green' | 'red' | 'purple' | 'yellow';
  subtitle?: string;
}> = ({ label, value, icon, color, subtitle }) => {
  const colorClasses = {
    teal: 'text-accent-teal bg-accent-teal/10 border-accent-teal/30',
    green: 'text-green-400 bg-green-400/10 border-green-400/30',
    red: 'text-red-400 bg-red-400/10 border-red-400/30',
    purple: 'text-accent-purple bg-accent-purple/10 border-accent-purple/30',
    yellow: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
  };
  const iconBg = {
    teal: 'bg-accent-teal/20',
    green: 'bg-green-400/20',
    red: 'bg-red-400/20',
    purple: 'bg-accent-purple/20',
    yellow: 'bg-yellow-400/20',
  };

  return (
    <div className="bg-dark-card border border-dark-border rounded-xl p-5 hover:bg-dark-card-hover transition-all duration-200 group">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-400 font-medium">{label}</p>
          <p className={`text-3xl font-bold mt-1 ${colorClasses[color].split(' ')[0]}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${iconBg[color]} transition-transform group-hover:scale-110`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const ClaimsStatusScreen = () => {
  const { allUsers } = useAuth();
  const SS_KEY = 'claimsStatus';
  const loadSS = () => {
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const saved = useRef(loadSS());
  const needsResume = useRef(saved.current?.processingStatus?.status === 'processing');

  const [claimInput, setClaimInput] = useState(saved.current?.claimInput || '');
  const [claimIds, setClaimIds] = useState<string[]>(saved.current?.claimIds || []);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>(
    saved.current?.processingStatus || { status: 'idle', processed: 0, errors: 0, total: 0, message: '' }
  );
  const [sendEmail, setSendEmail] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [downloadCsv, setDownloadCsv] = useState(true);
  const [csvData, setCsvData] = useState<any[]>(saved.current?.csvData || []);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef(false);

  const saveSS = useCallback((overrides?: Record<string, any>) => {
    try {
      const prev = loadSS() || {};
      sessionStorage.setItem(SS_KEY, JSON.stringify({ ...prev, ...overrides }));
    } catch {}
  }, []);

  useEffect(() => {
    saveSS({ claimInput, claimIds, processingStatus, csvData });
  }, [claimInput, claimIds, processingStatus, csvData, saveSS]);

  // Get filtered email suggestions based on input (active users only, excluding already selected)
  const emailSuggestions = useMemo(() => {
    if (!allUsers || !emailInput) return [];
    const query = emailInput.toLowerCase();
    return allUsers
      .filter((user: any) =>
        user.active === true &&
        !emailRecipients.includes(user.email) && (
          user.email?.toLowerCase().includes(query) ||
          user.name?.toLowerCase().includes(query)
        )
      )
      .slice(0, 5);
  }, [allUsers, emailInput, emailRecipients]);

  // Add email to recipients list
  const addEmailRecipient = useCallback((email: string) => {
    const trimmedEmail = email.trim().toLowerCase();
    if (trimmedEmail && trimmedEmail.includes('@') && !emailRecipients.includes(trimmedEmail)) {
      setEmailRecipients(prev => [...prev, trimmedEmail]);
      setEmailInput('');
      setShowEmailSuggestions(false);
    }
  }, [emailRecipients]);

  // Remove email from recipients list
  const removeEmailRecipient = useCallback((email: string) => {
    setEmailRecipients(prev => prev.filter(e => e !== email));
  }, []);

  // Handle email input key press
  const handleEmailKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmailRecipient(emailInput);
    } else if (e.key === 'Backspace' && !emailInput && emailRecipients.length > 0) {
      setEmailRecipients(prev => prev.slice(0, -1));
    }
  }, [emailInput, emailRecipients, addEmailRecipient]);

  // Parse claim IDs from text input
  const parseClaimIds = useCallback((text: string): string[] => {
    const ids = text
      .split(/[\n,\s\t]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0);
    return Array.from(new Set(ids));
  }, []);

  // Handle text input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setClaimInput(text);
    setClaimIds(parseClaimIds(text));
  };

  // Handle CSV file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) return;

      const lines = text.split('\n');
      const header = lines[0]?.toLowerCase() || '';
      const claimIdIndex = header.split(',').findIndex(col =>
        col.trim().includes('claim') || col.trim().includes('id')
      );

      const ids: string[] = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const columns = line.split(',');
        const id = claimIdIndex >= 0 ? columns[claimIdIndex]?.trim() : columns[0]?.trim();
        if (id && id.length > 0) {
          ids.push(id.replace(/"/g, ''));
        }
      }

      const uniqueIds = Array.from(new Set(ids));
      setClaimIds(prev => Array.from(new Set([...prev, ...uniqueIds])));
      setClaimInput((prev: string) => prev + (prev ? '\n' : '') + uniqueIds.join('\n'));
    };
    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Generate and download CSV
  const generateCsvDownload = useCallback((data: any[]) => {
    if (!data || data.length === 0) return;

    const headers = [
      'claim_id', 'policy_id', 'insurance', 'first_name', 'last_name',
      'start_date', 'end_date', 'facility', 'claim_amount', 'payment_amount',
      'status', 'category', 'finalized_date', 'control_number', 'check_number'
    ];

    const escape = (v: any) => {
      const s = String(v ?? '');
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/\"/g, '""')}"`;
      return s;
    };

    const lines = [
      headers.join(','),
      ...data.map((r: any) => headers.map(h => escape(r[h])).join(','))
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `claims_status_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  // Core batch runner — can start fresh or resume from a given batch index
  const runBatches = useCallback(async (
    ids: string[],
    startBatch: number,
    priorResults: any[],
    priorProcessed: number,
    priorErrors: number
  ) => {
    const BATCH_SIZE = 5;
    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      batches.push(ids.slice(i, i + BATCH_SIZE));
    }

    const newSessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    cancelRef.current = false;

    let totalProcessed = priorProcessed;
    let totalErrors = priorErrors;
    const allResults: any[] = [...priorResults];

    setProcessingStatus({
      status: 'processing',
      processed: totalProcessed,
      errors: totalErrors,
      total: ids.length,
      message: startBatch > 0
        ? `Resuming from batch ${startBatch + 1} of ${batches.length}...`
        : `Processing ${ids.length} claims...`
    });

    try {
      for (let i = startBatch; i < batches.length; i++) {
        if (cancelRef.current) {
          setProcessingStatus({
            status: 'completed',
            processed: totalProcessed,
            errors: totalErrors,
            total: ids.length,
            message: `Cancelled: ${totalProcessed} processed before stopping`
          });
          if (downloadCsv && allResults.length > 0) {
            setCsvData(allResults);
            generateCsvDownload(allResults);
          }
          saveSS({ completedBatch: null, pendingResults: null });
          setSessionId(null);
          return;
        }

        const batch = batches[i];
        const isLastBatch = i === batches.length - 1;

        setProcessingStatus(prev => ({
          ...prev,
          message: `Processing batch ${i + 1} of ${batches.length}...`
        }));

        try {
          const response = await axios.post(`${baseUrl}/claims/status/process`, {
            claim_ids: batch,
            session_id: newSessionId,
            send_email: isLastBatch && sendEmail,
            email_recipients: isLastBatch && emailRecipients.length > 0 ? emailRecipients : undefined,
            upload_to_s3: true
          }, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 120000
          });

          const data = response.data;
          totalProcessed += data.processed || 0;
          totalErrors += data.errors || 0;

          if (data.results) {
            allResults.push(...data.results);
          }

          // Save progress so we can resume if the user leaves
          saveSS({
            completedBatch: i + 1,
            pendingResults: allResults,
            pendingProcessed: totalProcessed,
            pendingErrors: totalErrors
          });

          if (data.cancelled) {
            setProcessingStatus({
              status: 'completed',
              processed: totalProcessed,
              errors: totalErrors,
              total: ids.length,
              message: `Cancelled: ${totalProcessed} processed before stopping`
            });
            if (downloadCsv && allResults.length > 0) {
              setCsvData(allResults);
              generateCsvDownload(allResults);
            }
            saveSS({ completedBatch: null, pendingResults: null });
            setSessionId(null);
            return;
          }

          setProcessingStatus(prev => ({
            ...prev,
            processed: totalProcessed,
            errors: totalErrors,
            message: `Processed ${totalProcessed} of ${ids.length} claims...`
          }));

        } catch (batchError: any) {
          console.error(`Batch ${i + 1} failed:`, batchError);
          totalErrors += batch.length;

          saveSS({
            completedBatch: i + 1,
            pendingResults: allResults,
            pendingProcessed: totalProcessed,
            pendingErrors: totalErrors
          });

          setProcessingStatus(prev => ({
            ...prev,
            errors: totalErrors,
            message: `Error in batch ${i + 1}, continuing...`
          }));
        }
      }

      setProcessingStatus({
        status: 'completed',
        processed: totalProcessed,
        errors: totalErrors,
        total: ids.length,
        message: `Completed: ${totalProcessed} processed, ${totalErrors} errors`
      });

      if (downloadCsv && allResults.length > 0) {
        setCsvData(allResults);
        generateCsvDownload(allResults);
      }

      saveSS({ completedBatch: null, pendingResults: null });

    } catch (error: any) {
      console.error('Failed to process claims:', error);
      setProcessingStatus({
        status: 'error',
        processed: totalProcessed,
        errors: totalErrors + (ids.length - totalProcessed - totalErrors),
        total: ids.length,
        message: error.response?.data?.detail || error.message || 'Failed to process claims'
      });
      saveSS({ completedBatch: null, pendingResults: null });
    }

    setSessionId(null);
  }, [sendEmail, emailRecipients, downloadCsv, generateCsvDownload, saveSS]);

  // Process claims — starts fresh
  const processClaimsAPI = useCallback(async () => {
    if (claimIds.length === 0) return;
    if (!sendEmail && !downloadCsv) return;
    setCsvData([]);
    saveSS({ completedBatch: null, pendingResults: null });
    await runBatches(claimIds, 0, [], 0, 0);
  }, [claimIds, sendEmail, downloadCsv, runBatches, saveSS]);

  // Auto-resume on mount if processing was interrupted
  useEffect(() => {
    if (!needsResume.current) return;
    needsResume.current = false;
    const s = saved.current;
    if (!s?.claimIds?.length) return;
    const startBatch = s.completedBatch || 0;
    const priorResults = s.pendingResults || [];
    const priorProcessed = s.pendingProcessed || 0;
    const priorErrors = s.pendingErrors || 0;
    runBatches(s.claimIds, startBatch, priorResults, priorProcessed, priorErrors);
  }, [runBatches]);

  // Cancel processing - calls backend to stop API calls
  const cancelProcessing = useCallback(async () => {
    cancelRef.current = true;
    setProcessingStatus(prev => ({
      ...prev,
      message: 'Cancelling...'
    }));

    // Tell backend to stop (saves API costs!)
    if (sessionId) {
      try {
        await axios.post(`${baseUrl}/claims/status/cancel`, {
          session_id: sessionId
        }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 5000
        });
      } catch (err) {
        console.error('Failed to send cancel to backend:', err);
      }
    }
  }, [sessionId]);

  // Manual download trigger
  const handleManualDownload = useCallback(() => {
    if (csvData.length > 0) {
      generateCsvDownload(csvData);
    }
  }, [csvData, generateCsvDownload]);

  // Clear all inputs
  const clearAll = useCallback(() => {
    setClaimInput('');
    setClaimIds([]);
    setCsvData([]);
    setProcessingStatus({
      status: 'idle',
      processed: 0,
      errors: 0,
      total: 0,
      message: ''
    });
  }, []);

  // Remove a single claim ID
  const removeClaimId = useCallback((idToRemove: string) => {
    setClaimIds(prev => prev.filter(id => id !== idToRemove));
    setClaimInput((prev: string) => {
      const ids = parseClaimIds(prev).filter(id => id !== idToRemove);
      return ids.join('\n');
    });
  }, [parseClaimIds]);

  const isEmailValid = !sendEmail || (sendEmail && emailRecipients.length > 0);
  const canProcess = claimIds.length > 0 && (sendEmail || downloadCsv) && isEmailValid && processingStatus.status !== 'processing';
  const progressPercent = processingStatus.total > 0 ? Math.round((processingStatus.processed / processingStatus.total) * 100) : 0;

  return (
    <LayoutComponent
      header={null}
      content={
        <div className="w-full h-full px-4 md:px-6 lg:px-8 py-4 md:py-6 overflow-auto">
          <div className="w-full space-y-6">

            {/* Header with Gradient */}
            <div className="relative overflow-hidden bg-gradient-to-br from-dark-card via-dark-card to-accent-teal/10 border border-dark-border rounded-2xl p-6 md:p-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-accent-teal/20 to-accent-purple/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-accent-teal to-accent-purple rounded-xl shadow-lg shadow-accent-teal/20">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-white">Claims Status</h1>
                    <p className="text-gray-400 mt-1">Query insurance payer claim statuses in real-time</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Claims Queued"
                value={claimIds.length}
                icon={<FileText className="w-6 h-6 text-accent-teal" />}
                color="teal"
                subtitle="Ready to process"
              />
              <StatCard
                label="Processed"
                value={processingStatus.processed}
                icon={<CheckCircle className="w-6 h-6 text-green-400" />}
                color="green"
                subtitle={processingStatus.status === 'processing' ? 'In progress...' : 'Completed'}
              />
              <StatCard
                label="Errors"
                value={processingStatus.errors}
                icon={<XCircle className="w-6 h-6 text-red-400" />}
                color="red"
                subtitle="Failed claims"
              />
              <StatCard
                label="Success Rate"
                value={processingStatus.total > 0 ? `${Math.round((processingStatus.processed / processingStatus.total) * 100)}%` : '-'}
                icon={<BarChart2 className="w-6 h-6 text-accent-purple" />}
                color="purple"
                subtitle={processingStatus.total > 0 ? `${processingStatus.processed}/${processingStatus.total}` : 'No data'}
              />
            </div>

            {/* Processing Status Bar */}
            {processingStatus.status !== 'idle' && (
              <div className={`rounded-xl p-4 border ${
                processingStatus.status === 'processing' ? 'bg-yellow-500/5 border-yellow-500/30' :
                processingStatus.status === 'completed' ? 'bg-green-500/5 border-green-500/30' :
                'bg-red-500/5 border-red-500/30'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {processingStatus.status === 'processing' && <Clock className="w-5 h-5 text-yellow-400 animate-spin" />}
                    {processingStatus.status === 'completed' && <CheckCircle className="w-5 h-5 text-green-400" />}
                    {processingStatus.status === 'error' && <AlertCircle className="w-5 h-5 text-red-400" />}
                    <span className="text-white font-medium">{processingStatus.message}</span>
                  </div>
                  {processingStatus.status === 'completed' && csvData.length > 0 && (
                    <button
                      onClick={handleManualDownload}
                      className="px-4 py-2 bg-gradient-to-r from-accent-teal to-accent-purple rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Report
                    </button>
                  )}
                </div>
                <div className="h-2 bg-dark-input rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      processingStatus.status === 'processing' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 animate-pulse' :
                      processingStatus.status === 'completed' ? 'bg-gradient-to-r from-green-400 to-green-500' :
                      'bg-gradient-to-r from-red-400 to-red-500'
                    }`}
                    style={{ width: `${processingStatus.status === 'processing' ? Math.max(progressPercent, 10) : 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Main Content Grid */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Input Section - 2 columns */}
              <div className="md:col-span-2 bg-dark-card border border-dark-border rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-accent-teal" />
                  Enter Claim IDs
                </h2>
                <textarea
                  value={claimInput}
                  onChange={handleInputChange}
                  placeholder="Paste claim IDs here (one per line, comma, or space separated)&#10;&#10;Example:&#10;281379314&#10;281493709&#10;281588822"
                  className="w-full h-48 bg-dark-input border border-dark-border rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-accent-teal focus:ring-1 focus:ring-accent-teal/50 transition-all resize-none font-mono text-sm"
                />

                {/* Claim Tags */}
                {claimIds.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-400">
                        <span className="text-accent-teal font-semibold">{claimIds.length}</span> claim{claimIds.length !== 1 ? 's' : ''} queued
                      </span>
                      <button
                        onClick={clearAll}
                        className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                      {claimIds.slice(0, 20).map((id) => (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-teal/10 border border-accent-teal/30 rounded-lg text-sm text-accent-teal font-mono group hover:bg-accent-teal/20 transition-colors"
                        >
                          {id}
                          <button
                            onClick={() => removeClaimId(id)}
                            className="text-gray-500 hover:text-red-400 transition-colors"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      {claimIds.length > 20 && (
                        <span className="px-3 py-1.5 text-sm text-gray-400">
                          +{claimIds.length - 20} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Sidebar */}
              <div className="space-y-6">
                {/* Upload CSV */}
                <div className="bg-dark-card border border-dark-border rounded-2xl p-6">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-accent-purple" />
                    Upload CSV
                  </h3>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-dark-border rounded-xl p-6 cursor-pointer hover:border-accent-purple hover:bg-accent-purple/5 transition-all group"
                  >
                    <div className="p-3 bg-accent-purple/10 rounded-xl group-hover:bg-accent-purple/20 transition-colors">
                      <Upload className="w-6 h-6 text-accent-purple" />
                    </div>
                    <span className="text-sm text-gray-400 text-center">
                      Drop file or click to browse
                    </span>
                  </label>
                </div>

                {/* Output Options */}
                <div className="bg-dark-card border border-dark-border rounded-2xl p-6">
                  <h3 className="text-sm font-semibold text-white mb-4">Output Options</h3>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      downloadCsv ? 'bg-accent-teal/10 border-accent-teal/50' : 'border-dark-border hover:bg-dark-input'
                    }`}>
                      <input
                        type="checkbox"
                        checked={downloadCsv}
                        onChange={(e) => setDownloadCsv(e.target.checked)}
                        className="w-4 h-4 rounded accent-accent-teal"
                      />
                      <Download className={`w-5 h-5 ${downloadCsv ? 'text-accent-teal' : 'text-gray-400'}`} />
                      <span className={downloadCsv ? 'text-white' : 'text-gray-400'}>Download Report</span>
                    </label>

                    <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      sendEmail ? 'bg-accent-purple/10 border-accent-purple/50' : 'border-dark-border hover:bg-dark-input'
                    }`}>
                      <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        className="w-4 h-4 rounded accent-accent-purple"
                      />
                      <Mail className={`w-5 h-5 ${sendEmail ? 'text-accent-purple' : 'text-gray-400'}`} />
                      <span className={sendEmail ? 'text-white' : 'text-gray-400'}>Email Report</span>
                    </label>

                    {sendEmail && (
                      <div className="mt-3 space-y-2">
                        {emailRecipients.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {emailRecipients.map((email) => (
                              <span
                                key={email}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-accent-purple/20 border border-accent-purple/40 rounded-full text-xs text-white"
                              >
                                {email.split('@')[0]}
                                <button
                                  onClick={() => removeEmailRecipient(email)}
                                  className="text-gray-400 hover:text-red-400"
                                >
                                  &times;
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="relative">
                          <input
                            ref={emailInputRef}
                            type="email"
                            value={emailInput}
                            onChange={(e) => {
                              setEmailInput(e.target.value);
                              setShowEmailSuggestions(true);
                            }}
                            onFocus={() => setShowEmailSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowEmailSuggestions(false), 200)}
                            onKeyDown={handleEmailKeyDown}
                            placeholder="Search users..."
                            className="w-full bg-dark-input border border-dark-border rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-accent-purple text-sm"
                          />
                          {showEmailSuggestions && emailSuggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-dark-card border border-dark-border rounded-xl shadow-xl z-50 overflow-hidden">
                              {emailSuggestions.map((user: any, index: number) => (
                                <button
                                  key={user.userid}
                                  type="button"
                                  onClick={() => addEmailRecipient(user.email)}
                                  className={`w-full px-3 py-2 text-left hover:bg-dark-card-hover transition-colors flex items-center gap-2 ${
                                    index !== emailSuggestions.length - 1 ? 'border-b border-dark-border/50' : ''
                                  }`}
                                >
                                  {user.profile_photo ? (
                                    <img src={user.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-accent-teal to-accent-purple flex items-center justify-center">
                                      <span className="text-white text-xs font-semibold">
                                        {user.name?.charAt(0) || '?'}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white truncate">{user.name}</div>
                                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Validation */}
                  {claimIds.length > 0 && !sendEmail && !downloadCsv && (
                    <div className="mt-3 text-amber-400 text-xs flex items-center gap-1.5 bg-amber-500/10 rounded-lg p-2">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Select at least one output
                    </div>
                  )}
                  {sendEmail && !isEmailValid && (
                    <div className="mt-3 text-amber-400 text-xs flex items-center gap-1.5 bg-amber-500/10 rounded-lg p-2">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Add email recipient
                    </div>
                  )}
                </div>

                {/* Process / Stop Buttons */}
                {processingStatus.status === 'processing' ? (
                  <button
                    onClick={cancelProcessing}
                    className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all bg-gradient-to-r from-red-500 to-red-600 text-white hover:opacity-90 active:scale-[0.98] shadow-lg shadow-red-500/20"
                  >
                    <StopCircle className="w-5 h-5" />
                    Stop Processing
                  </button>
                ) : (
                  <button
                    onClick={processClaimsAPI}
                    disabled={!canProcess}
                    className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                      !canProcess
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-accent-teal to-accent-purple text-white hover:opacity-90 active:scale-[0.98] shadow-lg shadow-accent-teal/20'
                    }`}
                  >
                    <Zap className="w-5 h-5" />
                    Check Claims Status
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default ClaimsStatusScreen;
